<script setup lang="ts">
import { computed, ref } from 'vue'
import ButtonLink from '../UI/Button/ButtonLink.vue'
import type { DocumentSigner } from '../../../../Types/document'
import { useI18n } from 'vue-i18n'
const { showSuccess } = useModalMessage()
import MessageForm from '../Message/MessageForm.vue'
import { useModalMessage } from '../../../../Composables/useModalMessage.ts'

interface Props {
  documentSigner: DocumentSigner
  documentSignId: number
  contractId: number
  disabled: boolean
}

const props = defineProps<Props>()
const defaultRecipientIds = computed(() => {
  return props.documentSigner?.signer?.id ? [props.documentSigner.signer.id] : []
})

const templateInternalName = computed<string>(() => {
  return props.documentSigner.signsContractDocument
    ? 'contract_edit_link_enhanced_sign'
    : 'document_sign_link_enhanced_sign'
})

const { t, d } = useI18n()

const signButtonLabel = computed<string>(() => {
  return `contract.document.${props.documentSigner.validator ? 'validateNow' : 'signNow'}`
})

const eSignUrl = computed<string>(() => {
  return `/sign/${props.documentSigner.id}?token=${props.documentSigner.token}&validation=${props.documentSigner.validator ? 'true' : 'false'}`
})

const signButtonType = computed<'disabled' | 'outline_success'>(() => {
  return props.disabled ? 'disabled' : 'outline_success'
})

const signedAt: string | null | undefined = props.documentSigner.signedAt

function emailSent(documentSigner: DocumentSigner): void {
  showSuccess(t('contract.document.sendSign.signTo', { name: `${documentSigner?.fullname}` }))
}
</script>

<template>
  <div class="border border-blue rounded-xl p-3 mb-3">
    <p class="text-center font-weight-bold mb-3">
      {{ documentSigner.fullname }}
    </p>

    <div v-if="!signedAt" class="col-12 d-flex justify-content-center py-2">
      <ButtonLink
        margin="no"
        size="large"
        class="mx-2"
        e2e-button="sendSignerSign"
        external
        :label="signButtonLabel"
        :href="eSignUrl"
        :type="signButtonType"
      />

      <MessageForm
        :ref="'messageForm' + props.documentSigner.id"
        :context-id="contractId"
        context="AppBundle/Entity/Contract"
        :master-id="props.documentSigner.id"
        master="DocumentBundle/Entity/DocumentSigner"
        :additional-contexts="[
          { context: 'DocumentBundle\\Entity\\DocumentSigner', contextId: props.documentSigner.id },
        ]"
        media="EMAIL"
        select-first-template
        :internal-name="templateInternalName"
        :selected-recipient-id="props.documentSigner.signer?.id"
        :can-save-message="false"
        button-type="outline_info"
        button-size="large"
        button-label="contract.document.sendMail"
        :default-recipient-ids="defaultRecipientIds"
        @message-sent="emailSent(props.documentSigner)"
      />
    </div>
    <div v-if="signedAt" class="d-flex justify-content-center align-items-center pt-3">
      <i class="far fa-clock mr-1"></i>
      <span>{{ t('document.signedAt', { date: d(signedAt, 'datetime') }) }}</span>
    </div>
  </div>
</template>
